/**
 * ========================================
 * ONBOARDING ("GETTING STARTED") SERVICE
 * ========================================
 * The required first-run step: the learner says who they are (Student + class 1-10 + board, Professional, or
 * Teacher) and the backend auto-assigns the matching published courses.
 *
 * Endpoints: GET /api/v1/onboarding/status, POST /api/v1/onboarding/complete,
 *            GET /api/v1/onboarding/course-choices, POST /api/v1/onboarding/choose-courses
 *
 * Matching: class AND board -> auto-assigned; class only (other/no board) -> offered as the 2nd choice;
 * everything else -> "All courses". The default course (English speaking) is auto-assigned to everyone.
 *
 * NOTE: raw snake_case, matching the backend exactly (see response-transformer.ts).
 */

import { apiClient } from './client';

export type LearnerType = 'student' | 'professional' | 'teacher';
export type Board = 'CBSE' | 'State Board' | 'ICSE';

export interface OnboardingOptions {
  learner_types: LearnerType[];
  standards: number[];
  boards: Board[];
}

export interface OnboardingStatus {
  completed: boolean;
  /** False once completed, and for admins (who are never forced through Getting Started). */
  required: boolean;
  learner_type: LearnerType | null;
  standard: number | null;
  board: Board | null;
  options: OnboardingOptions;
}

export interface AssignedCourse {
  id: string;
  name: string;
  code?: string | null;
  standard?: string | null;
}

export interface CourseChoice {
  id: string;
  name: string;
  description?: string | null;
  code?: string | null;
  standard?: string | null;
  board?: string | null;
}

/** Courses the learner can still pick, best fit first (already-enrolled ones left out). */
export interface CourseChoices {
  /** 2nd choice: same class, another (or no) board */
  suggested: CourseChoice[];
  /** every other published course -- the "All courses" option */
  others: CourseChoice[];
}

export interface OnboardingResult {
  status: OnboardingStatus;
  assigned_courses: AssignedCourse[];
  total_matching_courses: number;
  /** true when a course matched the learner's class AND board (or learner type); false -> offer `choices` */
  matched: boolean;
  choices: CourseChoices;
}

export interface OnboardingAnswers {
  learner_type: LearnerType;
  standard?: number | null;
  board?: Board | null;
}

interface Envelope<T> {
  status: string;
  message: string;
  data: T;
}

// The gate asks "is Getting Started still required?" on every protected route, so remember the answer per user for
// the life of the page instead of calling the API on each navigation.
const requiredCache = new Map<string, boolean>();

class OnboardingService {
  private baseUrl = '/api/v1/onboarding';

  // apiClient already converts failures into a thrown APIError, so callers just catch.
  async getStatus(): Promise<OnboardingStatus> {
    const response = await apiClient.get<Envelope<OnboardingStatus>>(`${this.baseUrl}/status`);
    return response.data.data;
  }

  async complete(answers: OnboardingAnswers): Promise<OnboardingResult> {
    const response = await apiClient.post<Envelope<OnboardingResult>>(`${this.baseUrl}/complete`, answers);
    return response.data.data;
  }

  async getCourseChoices(): Promise<CourseChoices> {
    const response = await apiClient.get<Envelope<CourseChoices>>(`${this.baseUrl}/course-choices`);
    return response.data.data;
  }

  /** Enrol the caller in the courses they picked; returns the ones newly added. */
  async chooseCourses(courseIds: string[]): Promise<AssignedCourse[]> {
    const response = await apiClient.post<Envelope<{ assigned_courses: AssignedCourse[] }>>(
      `${this.baseUrl}/choose-courses`, { course_ids: courseIds },
    );
    return response.data.data.assigned_courses;
  }

  /** Cached "is it still required" for `userId`; undefined until first fetched. */
  getCachedRequired(userId: string): boolean | undefined {
    return requiredCache.get(userId);
  }

  setCachedRequired(userId: string, required: boolean): void {
    requiredCache.set(userId, required);
  }
}

export const onboardingService = new OnboardingService();
